import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  cancelExecutionInstance,
  createExecutionInstance,
  pauseExecutionInstance,
  resumeExecutionInstance,
  reprocessExecutionInstance,
  type CreateExecutionInput,
  type ExecutionDetailsPayload,
} from '../../../services/executionService';
import { executionKeys } from '../keys';
import { invalidateOpsCenter } from '../../../lib/invalidateOpsCenter';
import type { ProcessInstanceStatus } from '../../../types/workflow-v9';

interface UseExecutionMutationsOptions {
  organizationId?: string;
}

interface CreateExecutionPayload extends Omit<CreateExecutionInput, 'organizationId'> {}

interface MutationResult<TVariables = unknown, TResult = unknown> {
  mutate: (variables: TVariables) => Promise<TResult>;
  isLoading: boolean;
  error: Error | null;
}

type DetailSnapshot = {
  key: ReturnType<typeof executionKeys.detail>;
  previous?: ExecutionDetailsPayload;
};

export function useExecutionMutations({ organizationId }: UseExecutionMutationsOptions) {
  const queryClient = useQueryClient();

  const ensureOrg = () => {
    if (!organizationId) {
      throw new Error('organizationId é obrigatório');
    }
    return organizationId;
  };

  const invalidateList = () => {
    if (!organizationId) return;
    queryClient.invalidateQueries({ queryKey: executionKeys.lists(organizationId), exact: false });
    invalidateOpsCenter(queryClient);
  };

  const invalidateDetails = (executionId?: string | null) => {
    if (!organizationId || !executionId) return;
    queryClient.invalidateQueries({ queryKey: executionKeys.detail(organizationId, executionId) });
  };

  const snapshotDetail = (executionId: string, nextStatus?: ProcessInstanceStatus): DetailSnapshot | undefined => {
    if (!organizationId) return undefined;
    const key = executionKeys.detail(organizationId, executionId);
    const previous = queryClient.getQueryData<ExecutionDetailsPayload>(key);
    if (previous && nextStatus) {
      queryClient.setQueryData<ExecutionDetailsPayload>(key, {
        ...previous,
        instance: { ...previous.instance, status: nextStatus },
      });
    }
    return { key, previous };
  };

  const createMutation = useMutation({
    mutationFn: async (payload: CreateExecutionPayload) => {
      const orgId = ensureOrg();
      return createExecutionInstance({ ...payload, organizationId: orgId });
    },
    onSuccess: () => invalidateList(),
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ executionId, reason }: { executionId: string; reason?: string }) => {
      const orgId = ensureOrg();
      await cancelExecutionInstance({ organizationId: orgId, executionId, reason });
      return executionId;
    },
    onMutate: async ({ executionId }) => snapshotDetail(executionId, 'cancelled'),
    onError: (_error, _variables, context) => {
      if (context?.previous && context.key) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSuccess: (executionId) => {
      invalidateList();
      invalidateDetails(executionId);
    },
  });

  const pauseMutation = useMutation({
    mutationFn: async (executionId: string) => {
      const orgId = ensureOrg();
      await pauseExecutionInstance({ organizationId: orgId, executionId });
      return executionId;
    },
    onMutate: async (executionId) => snapshotDetail(executionId, 'on_hold'),
    onError: (_error, _vars, context) => {
      if (context?.previous && context.key) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSuccess: (executionId) => {
      invalidateList();
      invalidateDetails(executionId);
    },
  });

  const resumeMutation = useMutation({
    mutationFn: async (executionId: string) => {
      const orgId = ensureOrg();
      await resumeExecutionInstance({ organizationId: orgId, executionId });
      return executionId;
    },
    onMutate: async (executionId) => snapshotDetail(executionId, 'active'),
    onError: (_error, _vars, context) => {
      if (context?.previous && context.key) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSuccess: (executionId) => {
      invalidateList();
      invalidateDetails(executionId);
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: async ({ executionId, reason }: { executionId: string; reason?: string }) => {
      const orgId = ensureOrg();
      return reprocessExecutionInstance({ organizationId: orgId, executionId, reason });
    },
    onSuccess: (result, variables) => {
      invalidateList();
      invalidateDetails(variables.executionId);
      if (result?.id && organizationId) {
        invalidateDetails(result.id);
      }
    },
  });

  const wrap = <TVariables, TResult>(mutation: {
    mutateAsync: (variables: TVariables) => Promise<TResult>;
    isPending: boolean;
    error: Error | null | undefined;
  }): MutationResult<TVariables, TResult> => ({
    mutate: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error ?? null,
  });

  return useMemo(
    () => ({
      createExecution: wrap<CreateExecutionPayload, Awaited<ReturnType<typeof createExecutionInstance>>>(createMutation),
      cancelExecution: wrap<{ executionId: string; reason?: string }, string>(cancelMutation),
      pauseExecution: wrap<string, string>(pauseMutation),
      resumeExecution: wrap<string, string>(resumeMutation),
      reprocessExecution: wrap<{ executionId: string; reason?: string }, Awaited<ReturnType<typeof reprocessExecutionInstance>>>(
        reprocessMutation,
      ),
    }),
    [
      cancelMutation.error,
      cancelMutation.isPending,
      cancelMutation.mutateAsync,
      createMutation.error,
      createMutation.isPending,
      createMutation.mutateAsync,
      pauseMutation.error,
      pauseMutation.isPending,
      pauseMutation.mutateAsync,
      reprocessMutation.error,
      reprocessMutation.isPending,
      reprocessMutation.mutateAsync,
      resumeMutation.error,
      resumeMutation.isPending,
      resumeMutation.mutateAsync,
    ],
  );
}
